// src/pages/CustomerDashboard.jsx - FIXED VERSION with Dynamic Typography

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { signOutCustomer } from '../api/customerAuthService';
import { getSalonServices } from '../api/salonApi';
import { getSalonProducts } from '../api/productsApi';
import '../styles/products.css';
import { useModules } from '../contexts/ModuleContext';
import { 
  doc, 
  getDoc,
  onSnapshot, 
  collection, 
  query, 
  where,
  updateDoc
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { subscribeToCustomer } from '../api/walletService';
import { notifyWalletDebit } from '../utils/bookingNotifications';
import { calculateDeliveryCharge } from '../api/deliveryApi';
import { useProductDeliverySettings } from '../hooks/useProductDeliverySettings';
import { getPersonalisationForCustomer, claimPopupImpression } from '../api/portalPersonalisationService';
import PortalPopup from '../components/customer/PortalPopup';
import { HeroMedia, GalleryMedia } from '../components/customer/PortalMedia';

import '../styles/customer.css';
import '../styles/responsive-utils.css';
import '../styles/customer-responsive.css';

// Deduplicate services by name — one card per unique service name, with global price range
const deduplicateServicesByName = (servicesList) => {
  const groups = {};
  servicesList.forEach((svc) => {
    const key = svc.name;
    if (!groups[key]) groups[key] = [];
    groups[key].push(svc);
  });

  return Object.values(groups).map((group) => {
    const base = group[0];
    const imageUrl = group.find((s) => s.imageUrl)?.imageUrl || null;

    const prices = group.map((s) => {
      const lo = s.priceMin ?? s.price ?? null;
      const hi = s.priceMax ?? s.price ?? null;
      return { lo, hi };
    }).filter((p) => p.lo != null);

    const globalMin = prices.length ? Math.min(...prices.map((p) => Number(p.lo))) : null;
    const globalMax = prices.length ? Math.max(...prices.map((p) => Number(p.hi ?? p.lo))) : null;

    return {
      ...base,
      imageUrl,
      priceMin: globalMin,
      priceMax: globalMax,
      price: undefined,
    };
  });
};

// Helper function to safely render potentially object fields
const safeRender = (field) => {
  if (field === null || field === undefined) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object') {
    if (field.name) return field.name;
    if (field.description) return field.description;
    try {
      return JSON.stringify(field);
    } catch (e) {
      return '';
    }
  }
  return String(field);
};

// Helper function to get business hours array from either old or new format
const getBusinessHoursArray = (businessHours) => {
  if (!businessHours) return [];
  
  if (businessHours.regularHours && Array.isArray(businessHours.regularHours)) {
    return businessHours.regularHours;
  }
  
  if (Array.isArray(businessHours)) {
    return businessHours;
  }
  
  return [];
};

// Helper function to format time (HH:MM to 12-hour format)
function formatTime(time24) {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':').map(Number);
  
  if (isNaN(hours) || isNaN(minutes)) return '';
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

// Helper function to convert font size to CSS value
const getFontSizeValue = (fontSize) => {
  switch (fontSize) {
    case 'small':
      return '14px';
    case 'large':
      return '18px';
    case 'medium':
    default:
      return '16px';
  }
};

// Card name clamped to 2 lines so the row below (price / duration) sits at the
// same height across cards; names needing more than 2 lines get a "More" toggle.
// `prefix` selects the CSS namespace ('cd-product' or 'cd-service').
function ClampedName({ name, prefix }) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const nameRef = useRef(null);

  useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el || expanded) return;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [name, expanded]);

  return (
    <div className={`${prefix}-name-wrap${expanded ? ' expanded' : ''}`}>
      <h3 ref={nameRef} className={`${prefix}-name`}>{name}</h3>
      {(overflows || expanded) && (
        <button
          type="button"
          className={`${prefix}-name-toggle`}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(v => !v);
          }}
        >
          {expanded ? 'View Less ▲' : 'View More ▼'}
        </button>
      )}
    </div>
  );
}

function CustomerDashboard() {
  const params = useParams();
  const urlSalonId = params.salonId?.toLowerCase();
  const location = useLocation();

  const {
    updateSalonId,
    currentUser,
    currentCustomer
  } = useCustomerAuth();

  const [salonConfig, setSalonConfig] = useState(null);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [paynowEnabled, setPaynowEnabled] = useState(false);
  const [paynowQrUrl, setPaynowQrUrl] = useState('');
  const [showPaynowModal, setShowPaynowModal] = useState(false);
  const [paynowModalProduct, setPaynowModalProduct] = useState(null);
  const [expandedProductIds, setExpandedProductIds] = useState(new Set());
  const [productImagePreview, setProductImagePreview] = useState(null); // { url, name }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);
  const [cancellingAppointment, setCancellingAppointment] = useState(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletPayProduct, setWalletPayProduct] = useState(null);
  const [walletPaying, setWalletPaying] = useState(false);
  const [walletPayError, setWalletPayError] = useState('');

  // ── Pickup / Delivery fulfillment flow (shown before the PayNow/Wallet modal) ──
  const [fulfillmentProduct, setFulfillmentProduct] = useState(null); // { product, paymentMethod: 'paynow'|'wallet' }
  const [fulfillmentStep, setFulfillmentStep] = useState('choose'); // 'choose' | 'address' | 'result'
  const [deliveryAddressInput, setDeliveryAddressInput] = useState('');
  const [deliveryCalculating, setDeliveryCalculating] = useState(false);
  const [deliveryCalcError, setDeliveryCalcError] = useState('');
  const [deliveryQuote, setDeliveryQuote] = useState(null); // { distanceKm, tierName, charge, resolvedAddress }
  const { baseLocation: deliveryBaseLocation } = useProductDeliverySettings(urlSalonId);

  // Close the full-size product image viewer with the Escape key
  useEffect(() => {
    if (!productImagePreview) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') setProductImagePreview(null); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [productImagePreview]);

  // Block Ctrl/Cmd+S (save) and Ctrl/Cmd+P (print) while the manual is open
  useEffect(() => {
    if (!showManualModal) return;
    const blockSaveAndPrint = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['s', 'p'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', blockSaveAndPrint, { capture: true });
    return () => window.removeEventListener('keydown', blockSaveAndPrint, { capture: true });
  }, [showManualModal]);

  // Module availability
  const { isModuleEnabled, initializeForSalon } = useModules();
  useEffect(() => {
    if (urlSalonId) initializeForSalon(urlSalonId);
  }, [urlSalonId]); // eslint-disable-line react-hooks/exhaustive-deps
  const bookingsEnabled = isModuleEnabled('bookings');
  const walletEnabled = isModuleEnabled('promotions');

  // Live wallet balance, so "Pay with Wallet" always reflects the latest
  // balance without needing a page refresh.
  useEffect(() => {
    if (!currentCustomer?.email || !urlSalonId) return;
    const customerId = `${currentCustomer.email}_${urlSalonId}`;
    const unsub = subscribeToCustomer(customerId, (data) => {
      setWalletBalance(data?.metrics?.walletBalance || 0);
    });
    return unsub;
  }, [currentCustomer?.email, urlSalonId]);

  // Refs for sub-tab scroll navigation from CustomerNavigation
  const servicesSectionRef     = useRef(null);
  const businessHoursSectionRef = useRef(null);
  const contactInfoSectionRef   = useRef(null);

  const navigate = useNavigate();

  // ── Portal Personalisation Campaigns ──
  // Popup + hero/gallery images set up by the admin for this customer's segment. While a
  // campaign is live its hero/gallery replace the ones from the Images page.
  const [personalisation, setPersonalisation] = useState({ popup: null, heroImage: null, galleryImages: [] });
  const [showPersonalisationPopup, setShowPersonalisationPopup] = useState(false);

  useEffect(() => {
    if (!currentCustomer?.email || !urlSalonId) return undefined;
    let cancelled = false;
    const customerId = `${currentCustomer.email.toLowerCase()}_${urlSalonId}`;

    getPersonalisationForCustomer(urlSalonId, customerId)
      .then((result) => {
        if (cancelled) return;
        setPersonalisation(result);

        // Every visit to the home page may show the popup, until the admin's per-day / per-month
        // caps are reached
        if (!result.popup) return undefined;
        return claimPopupImpression(result.popup, customerId)
          .catch((err) => {
            console.warn('Popup frequency tracking failed, not showing popup:', err);
            return false;
          })
          .then((allowed) => {
            if (allowed && !cancelled) setShowPersonalisationPopup(true);
          });
      })
      // Personalisation is an enhancement — never break the dashboard if it can't load
      .catch((err) => console.error('Error loading portal personalisation:', err));

    return () => { cancelled = true; };
  }, [currentCustomer?.email, urlSalonId]);

  const closePersonalisationPopup = () => {
    setShowPersonalisationPopup(false);
  };

  const handlePersonalisationCta = () => {
    closePersonalisationPopup();
    navigate(`/BespokeBeauty/${urlSalonId}/customer/book`);
  };

  // Scroll to an element accounting for the fixed nav header height
  const scrollToWithNavOffset = (ref) => {
    if (!ref.current) return;
    const navEl = document.querySelector('.customer-nav-fixed-wrapper');
    const navHeight = navEl ? navEl.offsetHeight : 0;
    const top = ref.current.getBoundingClientRect().top + window.pageYOffset - navHeight - 8;
    window.scrollTo({ top, behavior: 'smooth' });
  };
  
  // Scroll to section when navigated from CustomerNavigation sub-tabs
  useEffect(() => {
    const section = location.state?.section;
    if (!section || loading) return;
    const refMap = {
      'services':       servicesSectionRef,
      'business-hours': businessHoursSectionRef,
    };
    const target = refMap[section];
    if (target?.current) {
      setTimeout(() => target.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [location.state?.section, loading]);

  // Function to check if action is within 24 hours
  const isWithin24Hours = (appointmentDateTime) => {
    if (!appointmentDateTime) return true;
    
    const now = new Date();
    const timeDifference = appointmentDateTime.getTime() - now.getTime();
    const hoursUntilAppointment = timeDifference / (1000 * 60 * 60);
    
    return hoursUntilAppointment < 24;
  };

  // Function to show temporary messages
  const showMessage = (message, isError = false) => {
    if (isError) {
      setError(message);
      setSuccessMessage('');
    } else {
      setSuccessMessage(message);
      setError('');
    }
    
    setTimeout(() => {
      setError('');
      setSuccessMessage('');
    }, 5000);
  };

  // Function to cancel appointment
  const handleCancelAppointment = async (appointment) => {
    try {
      if (isWithin24Hours(appointment.dateTimeRequested)) {
        showMessage('Cancellation unsuccessful, cancellation must be made at least 24 hours before the date/time of the request.', true);
        return;
      }

      setCancellingAppointment(appointment.id);
      
      const appointmentRef = doc(db, 'appointments', appointment.id);
      
      await updateDoc(appointmentRef, {
        status: 'cancelled',
        cancelledAt: new Date(),
        updatedAt: new Date(),
        cancelledBy: 'customer' // drives the admin alert bell
      });
      
      console.log('Appointment cancelled successfully');
      showMessage('Appointment cancelled successfully!');
      
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      showMessage('Failed to cancel appointment. Please try again or contact the salon.', true);
    } finally {
      setCancellingAppointment(null);
    }
  };

  // Function to handle reschedule
  const handleRescheduleAppointment = (appointment) => {
    if (isWithin24Hours(appointment.dateTimeRequested)) {
      showMessage('Rescheduling unsuccessful, rescheduling must be made at least 24 hours before the date/time of the request.', true);
      return;
    }

    navigate(`/BespokeBeauty/${urlSalonId}/customer/book`, {
      state: {
        isReschedule: true,
        originalAppointment: appointment,
        rescheduleData: {
          originalAppointmentId: appointment.id,
          appointmentId: appointment.appointmentId,
          staffId: appointment.staffId,
          staffName: appointment.staffName,
          serviceId: appointment.serviceRequested?.id,
          serviceName: appointment.serviceRequested?.name,
          serviceDuration: appointment.serviceRequested?.duration,
          servicePrice: appointment.serviceRequested?.price,
          originalDateTime: appointment.dateTimeRequested,
          notes: appointment.notes || ''
        }
      }
    });
  };

  // SALON CONFIG LOADING - FIXED to read from 'salons' collection
  useEffect(() => {
    const loadSalonConfig = async () => {
      if (!urlSalonId) {
        return;
      }
      
      try {
        console.log('Loading salon config for:', urlSalonId);
        setLoading(true);
        
        const salonDocRef = doc(db, 'salons', urlSalonId);
        const docSnap = await getDoc(salonDocRef);
        
        if (docSnap.exists()) {
          const config = docSnap.data();
          console.log('Salon config loaded successfully:', config);
          setSalonConfig(config);
          // Default to head office, or first location
          if (config.locations?.length > 0) {
            const headOffice = config.locations.find(l => l.isHeadOffice) || config.locations[0];
            setSelectedLocationId(headOffice.locationId);
          }
        } else {
          console.error('No salon configuration found');
          setError('No salon configuration found');
        }
        
      } catch (err) {
        console.error('Error loading salon config:', err);
        setError('Failed to load salon information: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (updateSalonId) {
      updateSalonId(urlSalonId);
    }
    
    loadSalonConfig();
  }, [urlSalonId, updateSalonId]);

  // Apply dynamic brand colors AND typography
  useEffect(() => {
    if (salonConfig?.branding) {
      const root = document.documentElement;
      
      // Apply colors
      if (salonConfig.branding.colors) {
        root.style.setProperty('--salon-primary', salonConfig.branding.colors.primary || '#E64C31');
        root.style.setProperty('--salon-secondary', salonConfig.branding.colors.secondary || '#FFBDB1');
        root.style.setProperty('--salon-accent', salonConfig.branding.colors.accent || '#F6F6F6');
        root.style.setProperty('--salon-background', salonConfig.branding.colors.background || '#FFFFFF');
        root.style.setProperty('--salon-text', salonConfig.branding.colors.text || '#333333');
      }
      
      // Apply typography
      if (salonConfig.branding.typography) {
        root.style.setProperty('--salon-heading-font', salonConfig.branding.typography.headingFont || 'Familjen Grotesk, sans-serif');
        root.style.setProperty('--salon-body-font', salonConfig.branding.typography.bodyFont || 'Wix Madefor Text, sans-serif');
        root.style.setProperty('--salon-font-size', getFontSizeValue(salonConfig.branding.typography.fontSize));
      }
    }
  }, [salonConfig]);

  // Derive PayNow enabled status directly from the already-loaded salonConfig.
  // This avoids a duplicate Firestore read and guarantees isActive is always checked.
  useEffect(() => {
    if (salonConfig) {
      const pc = salonConfig.paynowConfig;
      const isActive = pc?.isActive === true;
      setPaynowEnabled(isActive);
      setPaynowQrUrl(isActive ? (pc?.qrCodeImageUrl || '') : '');
    } else {
      setPaynowEnabled(false);
      setPaynowQrUrl('');
    }
  }, [salonConfig]);

  // SERVICES & PRODUCTS LOADING
  useEffect(() => {
    const fetchData = async () => {
      if (!urlSalonId) return;
      try {
        const servicesData = await getSalonServices(urlSalonId);
        setServices(servicesData);
      } catch (err) {
        console.error('Error fetching salon services:', err);
      }
      try {
        const productsData = await getSalonProducts(urlSalonId);
        setProducts(productsData);
      } catch (err) {
        console.error('Error fetching salon products:', err);
      }
      // PayNow config is derived from salonConfig (same Firestore doc, no extra read needed).
      // Handled in the salonConfig useEffect below.
    };
    fetchData();
  }, [urlSalonId]);

  // APPOINTMENTS LOADING - FIXED to properly query by customerId and salonId
  useEffect(() => {
    if (!currentUser || !urlSalonId) {
      setAppointmentsLoading(false);
      return;
    }

    console.log('Setting up appointments listener for:', {
      customerEmail: currentUser.email,
      salonId: urlSalonId
    });

    setAppointmentsLoading(true);

    const appointmentsRef = collection(db, 'appointments');
    const appointmentsQuery = query(
      appointmentsRef,
      where('customerEmail', '==', currentUser.email),
      where('salonId', '==', urlSalonId)
    );
    
    const unsubscribe = onSnapshot(appointmentsQuery, (snapshot) => {
      console.log('Appointments snapshot received, docs count:', snapshot.size);
      
      const appointments = [];
      const now = new Date();
      
      snapshot.forEach((doc) => {
        const appointmentData = doc.data();
        console.log('Processing appointment:', doc.id, appointmentData);
        
        // Only show pending, approved, or confirmed appointments
        if (['pending', 'approved', 'confirmed'].includes(appointmentData.status)) {
          let appointmentDateTime = null;
          
          if (appointmentData.dateTimeRequested) {
            if (appointmentData.dateTimeRequested.toDate) {
              appointmentDateTime = appointmentData.dateTimeRequested.toDate();
            } else if (typeof appointmentData.dateTimeRequested === 'string') {
              appointmentDateTime = new Date(appointmentData.dateTimeRequested);
            } else {
              appointmentDateTime = appointmentData.dateTimeRequested;
            }
          }
          
          // Only show future appointments
          if (appointmentDateTime && appointmentDateTime > now) {
            appointments.push({
              id: doc.id,
              ...appointmentData,
              dateTimeRequested: appointmentDateTime,
              dateTimeCreated: appointmentData.dateTimeCreated?.toDate ? 
                appointmentData.dateTimeCreated.toDate() : 
                new Date(appointmentData.dateTimeCreated)
            });
          }
        }
      });
      
      // Sort by appointment date
      appointments.sort((a, b) => a.dateTimeRequested - b.dateTimeRequested);
      
      console.log('Final upcoming appointments:', appointments);
      setUpcomingAppointments(appointments);
      setAppointmentsLoading(false);
    }, (error) => {
      console.error('Error in appointments listener:', error);
      setAppointmentsLoading(false);
    });

    return () => {
      console.log('Cleaning up appointments listener');
      unsubscribe();
    };
  }, [currentUser, urlSalonId]);

  // UTILITY FUNCTIONS
  const formatAppointmentDate = (dateTime) => {
    if (!dateTime) return '';

    return dateTime.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatAppointmentTime = (dateTime) => {
    if (!dateTime) return '';

    return dateTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatAppointmentDateTime = (dateTime) => {
    if (!dateTime) return '';

    return dateTime.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getDisplayStatus = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'Pending';
      case 'approved':
      case 'confirmed':
        return 'Confirmed';
      case 'rejected':
        return 'Rejected';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'status-pending';
      case 'approved':
      case 'confirmed':
        return 'status-confirmed';
      case 'rejected':
        return 'status-rejected';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-unknown';
    }
  };
  
  const handleSignOut = async () => {
    try {
      await signOutCustomer();
      navigate(`/BespokeBeauty/${urlSalonId}/customer/login`);
    } catch (err) {
      console.error('Error signing out:', err);
      setError('Failed to sign out');
    }
  };
  
  // #Revised UI_v2 — CE8: skeleton loader replaces spinner
  if (loading) {
    return (
      <div className="customer-container">
        <div className="skel-hero-bar" />
        <div className="skel-tiles-row">
          {[1,2,3].map(i => <div key={i} className="skel-tile-card" />)}
        </div>
        <div className="skel-section">
          {[1,2].map(i => (
            <div key={i} className="skel-appt-card">
              <div className="skel-avatar" />
              <div className="skel-lines">
                <div className="skel-line skel-line--lg" />
                <div className="skel-line skel-line--md" />
                <div className="skel-line skel-line--sm" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Drag-to-scroll handler for the services carousel (desktop mouse)
  const handleCarouselMouseDown = (e) => {
    const el = e.currentTarget;
    el.dataset.dragging = 'false';
    const startX = e.pageX - el.offsetLeft;
    const scrollLeft = el.scrollLeft;

    const onMove = (ev) => {
      el.dataset.dragging = 'true';
      const x = ev.pageX - el.offsetLeft;
      el.scrollLeft = scrollLeft - (x - startX);
    };
    const onUp = () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseup', onUp);
    };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseup', onUp);
  };

  // Render services as image + detail cards
  const renderServices = () => {
    const activeServices = deduplicateServicesByName(
      (services || []).filter(s => s.isActive !== false)
    );

    if (activeServices.length === 0) {
      return <p className="cd-services-empty">We offer a range of premium beauty treatments to help you look and feel your best.</p>;
    }

    return (
      <div className="cd-services-grid" onMouseDown={handleCarouselMouseDown}>
        {activeServices.map((service) => (
          <div key={service.id} className="cd-service-card">
            {/* Image area */}
            <div className="cd-service-image">
              {service.imageUrl
                ? <img src={service.imageUrl} alt={service.name} />
                : <div className="cd-service-image-placeholder" />
              }
              {service.category && (
                <span className="cd-service-category">{service.category}</span>
              )}
            </div>

            {/* Card body */}
            <div className="cd-service-body">
              <ClampedName name={service.name} prefix="cd-service" />

              <div className="cd-service-badges">
                <span className="cd-service-badge cd-service-badge--duration">
                  ⏱ {service.duration || 'N/A'} min
                </span>
              </div>

              {service.description && (
                <p className="cd-service-description">{service.description}</p>
              )}

              <button
                className="cd-service-book-btn"
                onClick={(e) => {
                  if (e.currentTarget.closest('.cd-services-grid')?.dataset.dragging === 'true') return;
                  if (!bookingsEnabled) {
                    scrollToWithNavOffset(contactInfoSectionRef);
                    return;
                  }
                  navigate(`/BespokeBeauty/${urlSalonId}/customer/book`, { state: { selectedService: service } });
                }}
              >
                Book Now
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  // Toggle description expanded state per product
  const toggleProductExpanded = (productId) => {
    setExpandedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  // Pay for a product purchase directly from the customer's wallet balance,
  // via the trusted backend route (never a client-side updateDoc — the
  // customer cannot be allowed to decrement their own balance directly).
  const handleConfirmPayWithWallet = async () => {
    if (!walletPayProduct || !currentUser) return;
    setWalletPaying(true);
    setWalletPayError('');
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const idToken = await currentUser.getIdToken();
      const productPrice = Number(walletPayProduct.price || 0);
      const deliveryCharge = Number(walletPayProduct.deliveryCharge || 0);
      const totalAmount = productPrice + deliveryCharge;
      const response = await fetch(`${API_BASE_URL}/api/wallet/pay-with-wallet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          salonId: urlSalonId,
          productId: walletPayProduct.id,
          productName: walletPayProduct.name,
          amount: totalAmount,
          price: productPrice,
          deliveryCharge,
          fulfillmentType: walletPayProduct.fulfillmentType || 'pickup',
          deliveryAddress: walletPayProduct.deliveryAddress || null,
          deliveryDistanceKm: walletPayProduct.deliveryDistanceKm || null,
          deliveryTierName: walletPayProduct.deliveryTierName || null,
          deliveryChargeAccepted: walletPayProduct.deliveryChargeAccepted ?? null
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Payment failed');
      }

      // Honours the salon's push_wallet_debit toggle and wallet_debit template (non-blocking)
      notifyWalletDebit({
        salonId: urlSalonId,
        customerEmail: currentUser.email,
        customerName: currentUser.displayName,
        amount: totalAmount,
        newBalance: Number(data.newBalance || 0),
        item: walletPayProduct.name
      });

      setSuccessMessage(`Payment successful! ${walletPayProduct.name} purchased.`);
      setTimeout(() => setSuccessMessage(''), 4000);
      setWalletPayProduct(null);
    } catch (err) {
      console.error('Error paying with wallet:', err);
      setWalletPayError(err.message || 'Payment failed. Please try again.');
    } finally {
      setWalletPaying(false);
    }
  };

  // Open a pre-filled mailto enquiry for a product when PayNow is not available
  const handleMakeEnquiry = (product) => {
    const email = salonConfig?.email || '';
    if (!email) return;
    const subject = encodeURIComponent(`Product Enquiry: ${product.name}`);
    const body = encodeURIComponent(
      `Hi,\n\nI would like to enquire about the following product:\n\nProduct: ${product.name}\nPrice: $${Number(product.price || 0).toFixed(2)}\n\nCould you please provide more details or let me know how I can purchase it?\n\nThank you.`
    );
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  // Open the pickup/delivery fulfillment flow for a product before showing
  // the PayNow QR or Wallet confirm modal.
  const openFulfillmentFlow = (product, paymentMethod) => {
    setFulfillmentProduct({ product, paymentMethod });
    setFulfillmentStep('choose');
    setDeliveryAddressInput('');
    setDeliveryQuote(null);
    setDeliveryCalcError('');
  };

  const closeFulfillmentFlow = () => setFulfillmentProduct(null);

  // Hands off to the existing PayNow QR / Wallet confirm modal, carrying the
  // chosen fulfillment details (delivery charge, address, etc.) along with it.
  const proceedToPayment = (fulfillmentDetails) => {
    const { product, paymentMethod } = fulfillmentProduct;
    const productWithDelivery = {
      ...product,
      deliveryCharge: fulfillmentDetails.deliveryCharge || 0,
      fulfillmentType: fulfillmentDetails.fulfillmentType,
      deliveryAddress: fulfillmentDetails.deliveryAddress || null,
      deliveryDistanceKm: fulfillmentDetails.deliveryDistanceKm || null,
      deliveryTierName: fulfillmentDetails.deliveryTierName || null,
      deliveryChargeAccepted: fulfillmentDetails.deliveryChargeAccepted ?? null
    };

    if (paymentMethod === 'paynow') {
      setPaynowModalProduct(productWithDelivery);
      setShowPaynowModal(true);
    } else {
      setWalletPayError('');
      setWalletPayProduct(productWithDelivery);
    }
    setFulfillmentProduct(null);
  };

  const choosePickup = () => {
    proceedToPayment({ fulfillmentType: 'pickup', deliveryCharge: 0 });
  };

  const chooseDelivery = () => setFulfillmentStep('address');

  const handleCalculateDeliveryCharge = async () => {
    if (!deliveryAddressInput.trim()) {
      setDeliveryCalcError('Please enter your delivery address.');
      return;
    }
    setDeliveryCalculating(true);
    setDeliveryCalcError('');
    try {
      const result = await calculateDeliveryCharge(urlSalonId, deliveryAddressInput.trim());
      setDeliveryQuote(result);
      setFulfillmentStep('result');
    } catch (err) {
      console.error('Error calculating delivery charge:', err);
      setDeliveryCalcError(err.message || 'Could not calculate delivery charge. Please check the address and try again.');
    } finally {
      setDeliveryCalculating(false);
    }
  };

  const acceptDeliveryCharge = () => {
    proceedToPayment({
      fulfillmentType: 'delivery',
      deliveryCharge: deliveryQuote.charge,
      deliveryAddress: deliveryAddressInput.trim(),
      deliveryDistanceKm: deliveryQuote.distanceKm,
      deliveryTierName: deliveryQuote.tierName,
      deliveryChargeAccepted: true
    });
  };

  const rejectDeliveryCharge = () => {
    proceedToPayment({
      fulfillmentType: 'delivery',
      deliveryCharge: 0,
      deliveryAddress: deliveryAddressInput.trim(),
      deliveryDistanceKm: deliveryQuote.distanceKm,
      deliveryTierName: deliveryQuote.tierName,
      deliveryChargeAccepted: false
    });
  };

  // Render products as image + detail cards
  const renderProducts = () => {
    const activeProducts = (products || []).filter(p => p.isActive !== false);
    if (activeProducts.length === 0) return null;
    return (
      <div className="cd-products-grid" onMouseDown={handleCarouselMouseDown}>
        {activeProducts.map((product) => {
          const isExpanded = expandedProductIds.has(product.id);
          const hasLongDesc = product.description && product.description.length > 80;
          return (
            <div key={product.id} className="cd-product-card">
              <div
                className={`cd-product-image${product.imageUrl ? ' cd-product-image--zoomable' : ''}`}
                onClick={product.imageUrl ? (e) => {
                  if (e.currentTarget.closest('.cd-products-grid')?.dataset.dragging === 'true') return;
                  setProductImagePreview({ url: product.imageUrl, name: product.name });
                } : undefined}
                onKeyDown={product.imageUrl ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setProductImagePreview({ url: product.imageUrl, name: product.name });
                  }
                } : undefined}
                role={product.imageUrl ? 'button' : undefined}
                tabIndex={product.imageUrl ? 0 : undefined}
                aria-label={product.imageUrl ? `View full size image of ${product.name}` : undefined}
              >
                {product.imageUrl
                  ? <img src={product.imageUrl} alt={product.name} draggable={false} />
                  : <div className="cd-product-image-placeholder" />
                }
                {product.category && (
                  <span className="cd-product-category">{product.category}</span>
                )}
              </div>
              <div className="cd-product-body">
                <ClampedName name={product.name} prefix="cd-product" />
                <div className="cd-product-badges">
                  <span className="cd-product-badge cd-product-badge--price">
                    ${Number(product.price || 0).toFixed(2)}
                  </span>
                </div>
                {product.description && (
                  <>
                    <p className={`cd-product-description${isExpanded ? ' expanded' : ''}`}>
                      {product.description}
                    </p>
                    {hasLongDesc && (
                      <button
                        className="cd-product-viewmore-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleProductExpanded(product.id);
                        }}
                      >
                        {isExpanded ? 'View Less ▲' : 'View More ▼'}
                      </button>
                    )}
                  </>
                )}
                <div className="cd-product-actions">
                  {paynowEnabled ? (
                    <button
                      className="cd-product-paynow-btn"
                      onClick={(e) => {
                        if (e.currentTarget.closest('.cd-products-grid')?.dataset.dragging === 'true') return;
                        openFulfillmentFlow(product, 'paynow');
                      }}
                    >
                      Buy via PayNow
                    </button>
                  ) : (
                    <button
                      className="cd-product-enquiry-btn"
                      onClick={(e) => {
                        if (e.currentTarget.closest('.cd-products-grid')?.dataset.dragging === 'true') return;
                        handleMakeEnquiry(product);
                      }}
                    >
                      Make Enquiry
                    </button>
                  )}
                  {walletEnabled && (
                    <button
                      className="cd-product-paynow-btn"
                      disabled={walletBalance < Number(product.price || 0)}
                      title={walletBalance < Number(product.price || 0) ? 'Insufficient wallet balance' : undefined}
                      onClick={(e) => {
                        if (e.currentTarget.closest('.cd-products-grid')?.dataset.dragging === 'true') return;
                        openFulfillmentFlow(product, 'wallet');
                      }}
                    >
                      Pay with Wallet
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Salon details — top portion: logo, hero image, gallery
  const renderSalonTop = () => {
    if (!salonConfig) return null;
    // Personalised images (when live for this customer) take priority over the Images page
    const heroImage = personalisation.heroImage || salonConfig.branding?.images?.hero;
    const galleryImages = personalisation.galleryImages.length > 0
      ? personalisation.galleryImages
      : salonConfig.branding?.images?.gallery;
    return (
      <div className="salon-details-container">
        <div className="salon-details">
          {/* Logo */}
          {salonConfig.branding?.logo?.url && (
            <div className={`salon-logo-section logo-${salonConfig.branding?.logo?.position || salonConfig.branding?.logoPosition || 'center'}`}>
              <img
                src={salonConfig.branding.logo.url}
                alt="Salon Logo"
                className="salon-detail-logo"
              />
            </div>
          )}

          {/* Hero Image */}
          {heroImage && (
            <div className="salon-hero-section">
              <HeroMedia src={heroImage} alt="Salon Hero" />
            </div>
          )}

          {/* Gallery */}
          {galleryImages && galleryImages.length > 0 && (
            <div className="salon-gallery-section">
              <h2 style={{ color: 'var(--salon-primary, #E64C31)' }}>Our Gallery</h2>
              <div className="salon-gallery-grid">
                {galleryImages.map((image, index) => (
                  <GalleryMedia key={index} src={image} alt={`Gallery ${index + 1}`} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Salon details — bottom portion: services, about, contact, hours, social
  const renderSalonBottom = () => {
    if (!salonConfig) return null;

    const locations = salonConfig.locations || [];
    const isMultiLocation = locations.length > 1;

    // Resolve which location object is active
    const activeLocation = isMultiLocation
      ? (locations.find(l => l.locationId === selectedLocationId) || locations[0])
      : null;

    // Contact fields for the active view
    const displayAddress = isMultiLocation
      ? (activeLocation?.address || 'Address not provided')
      : (salonConfig.address || 'Address not provided');
    const displayPhone = isMultiLocation
      ? (activeLocation?.phone || 'Phone not provided')
      : (salonConfig.phone || 'Phone not provided');

    // Business hours for the active view
    const resolveHours = () => {
      if (isMultiLocation && salonConfig.businessHours?.locations) {
        const locId = activeLocation?.locationId;
        const locHours = salonConfig.businessHours.locations[locId];
        if (locHours) return getBusinessHoursArray(locHours);
      }
      return getBusinessHoursArray(salonConfig.businessHours);
    };
    const businessHoursArray = resolveHours();

    // Location pill tabs (shared between Contact and Hours sections)
    const renderLocationPills = () => (
      <div className="location-pills">
        {locations.map(loc => (
          <button
            key={loc.locationId}
            className={`location-pill${selectedLocationId === loc.locationId ? ' location-pill--active' : ''}`}
            onClick={() => setSelectedLocationId(loc.locationId)}
          >
            {loc.locationName}
          </button>
        ))}
      </div>
    );

    return (
      <div className="salon-details-container">
        <div className="salon-details">
          {/* Services */}
          <div className="services-section" ref={servicesSectionRef} id="section-services">
            <h2>Our Services</h2>
            {renderServices()}
          </div>

          {/* Products */}
          {(() => { const p = renderProducts(); return p ? <div className="services-section" id="section-products"><h2>Our Products</h2>{p}</div> : null; })()}

          {/* About Us */}
          <div className="about-us-section">
            <h2 style={{ color: 'var(--salon-primary, #E64C31)' }}>About Us</h2>
            <p>{safeRender(salonConfig.aboutUs) || `At ${safeRender(salonConfig.branding?.name) || 'Our Salon'}, we believe in bringing out your natural beauty.`}</p>
          </div>

          {/* Location pills — one set above Contact Us, controls both Contact and Hours */}
          {isMultiLocation && (
            <div className="location-pills-row">
              {renderLocationPills()}
            </div>
          )}

          {/* Contact Info */}
          <div className="contact-info-section" ref={contactInfoSectionRef} id="section-contact">
            <h2>Contact Us</h2>
            <div className="contact-details">
              <p><strong>Address:</strong> {displayAddress}</p>
              <p><strong>Phone:</strong> {displayPhone}</p>
              <p><strong>Email:</strong> {salonConfig.email || 'Email not provided'}</p>
            </div>
          </div>

          {/* Business Hours */}
          <div className="business-hours-section" ref={businessHoursSectionRef} id="section-business-hours">
            <h2>Business Hours</h2>
            <ul className="hours-list">
              {businessHoursArray.map((hours) => (
                <li key={safeRender(hours.day)}>
                  <span className="day">{safeRender(hours.day)}</span>
                  <span className="hours">
                    {hours.isClosed ? 'Closed' : `${formatTime(hours.open)} - ${formatTime(hours.close)}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Follow Us */}
          <div className="follow-us-section">
            <h2>Follow Us</h2>
            <div className="social-media-links">
              {salonConfig.socialMedia?.instagram && (
                <a
                  href={salonConfig.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-media-link instagram"
                >
                  <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <span>Instagram</span>
                </a>
              )}
              {salonConfig.socialMedia?.facebook && (
                <a
                  href={salonConfig.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-media-link facebook"
                >
                  <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span>Facebook</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Status meta for appt-card badges (matches MyAppointments design)
  const APPT_STATUS_META = {
    pending:   { label: 'Pending',   cls: 'appt-status-pending'   },
    approved:  { label: 'Confirmed', cls: 'appt-status-confirmed' },
    confirmed: { label: 'Confirmed', cls: 'appt-status-confirmed' },
    completed: { label: 'Completed', cls: 'appt-status-completed' },
    cancelled: { label: 'Cancelled', cls: 'appt-status-cancelled' },
    rejected:  { label: 'Rejected',  cls: 'appt-status-rejected'  },
  };

  const renderDashboardApptCard = (appointment) => {
    const meta   = APPT_STATUS_META[appointment.status?.toLowerCase()] || { label: appointment.status, cls: '' };
    const locked = isWithin24Hours(appointment.dateTimeRequested);
    return (
      <div key={appointment.id} className="appt-card">
        <div className="appt-card-header">
          <div className="appt-card-service">
            <span className="appt-service-name">
              {appointment.serviceRequested?.name || 'Service'}
            </span>
            {appointment.serviceRequested?.duration && (
              <span className="appt-service-duration">
                {appointment.serviceRequested.duration} min
              </span>
            )}
          </div>
          <span className={`appt-status-badge ${meta.cls}`}>{meta.label}</span>
        </div>

        <div className="appt-card-body">
          <div className="appt-detail-row">
            <svg className="appt-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <rect x="1" y="3" width="14" height="12" rx="2"/>
              <line x1="5" y1="1" x2="5" y2="5"/>
              <line x1="11" y1="1" x2="11" y2="5"/>
              <line x1="1" y1="7" x2="15" y2="7"/>
            </svg>
            <span>{appointment.dateTimeRequested ? `${formatAppointmentDate(appointment.dateTimeRequested)} at ${formatAppointmentTime(appointment.dateTimeRequested)}` : 'Date not set'}</span>
          </div>

          <div className="appt-detail-row">
            <svg className="appt-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <circle cx="8" cy="5" r="3"/>
              <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/>
            </svg>
            <span>with {appointment.staffName || appointment.stylistName || 'Staff'}</span>
          </div>

          {appointment.notes && (
            <div className="appt-detail-row">
              <svg className="appt-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <rect x="2" y="2" width="12" height="12" rx="2"/>
                <line x1="5" y1="6" x2="11" y2="6"/>
                <line x1="5" y1="9" x2="9" y2="9"/>
              </svg>
              <span className="appt-notes">{appointment.notes}</span>
            </div>
          )}

          {locked && (
            <p className="appt-locked-note">Changes not allowed within 24 hours of appointment.</p>
          )}
        </div>

        {(appointment.status === 'pending' || appointment.status === 'approved' || appointment.status === 'confirmed') && (
          <div className="appt-card-actions">
            <button
              className="appt-btn appt-btn-reschedule"
              onClick={() => handleRescheduleAppointment(appointment)}
              disabled={locked}
              title={locked ? 'Cannot reschedule within 24 hours' : 'Reschedule'}
            >
              Reschedule
            </button>
            <button
              className="appt-btn appt-btn-cancel"
              onClick={() => handleCancelAppointment(appointment)}
              disabled={cancellingAppointment === appointment.id || locked}
              title={locked ? 'Cannot cancel within 24 hours' : 'Cancel'}
            >
              {cancellingAppointment === appointment.id ? 'Cancelling…' : 'Cancel'}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Upcoming Appointments section (rendered between gallery and salon bottom)
  const renderUpcomingAppointments = () => {
    if (appointmentsLoading) {
      return (
        <div className="promotions-section" style={{ margin: '0 auto 24px', width: '80%' }}>
          <div className="skel-appt-list">
            {[1, 2].map(i => (
              <div key={i} className="skel-appt-card">
                <div className="skel-avatar" />
                <div className="skel-lines">
                  <div className="skel-line skel-line--lg" />
                  <div className="skel-line skel-line--md" />
                  <div className="skel-line skel-line--sm" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (upcomingAppointments.length > 0) {
      return (
        <div className="promotions-section" style={{ margin: '0 auto 24px', width: '80%' }}>
          <div className="appt-section-heading">
            <h2>Upcoming Appointments</h2>
            {bookingsEnabled && (
              <button
                className="appt-book-btn"
                onClick={() => navigate(`/BespokeBeauty/${urlSalonId}/customer/book`)}
              >
                + Book New
              </button>
            )}
          </div>
          <div className="appt-list">
            {upcomingAppointments.map(a => renderDashboardApptCard(a))}
          </div>
        </div>
      );
    }

    return (
      <div className="empty-state-illustrated" style={{ padding: '30px 0' }}>
        <p className="empty-state-sub">Ready for a fresh look?</p>
        <button
          className="empty-state-cta"
          onClick={() => {
            if (!bookingsEnabled) {
              scrollToWithNavOffset(contactInfoSectionRef);
              return;
            }
            navigate(`/BespokeBeauty/${urlSalonId}/customer/book`);
          }}
        >
          Book Now
        </button>
      </div>
    );
  };

  return (
    <div className="customer-container">
      <main className="customer-main">

        {(error || successMessage) && (
          <div className="message-container">
            {error && (
              <div className="error-message">
                {error}
                <button className="close-message" onClick={() => setError('')} aria-label="Close message">×</button>
              </div>
            )}
            {successMessage && (
              <div className="success-message">
                {successMessage}
                <button className="close-message" onClick={() => setSuccessMessage('')} aria-label="Close message">×</button>
              </div>
            )}
          </div>
        )}

        {/* Personalised popup for targeted customers */}
        {showPersonalisationPopup && personalisation.popup && (
          <PortalPopup
            popup={personalisation.popup}
            onCta={handlePersonalisationCta}
            onClose={closePersonalisationPopup}
          />
        )}

        {/* Logo + Hero + Gallery */}
        {renderSalonTop()}

        {/* Upcoming Appointments — hidden entirely when Booking Management is disabled */}
        {bookingsEnabled && renderUpcomingAppointments()}

        {/* Services + About + Contact + Hours + Social */}
        {renderSalonBottom()}

        <div className="customer-legal-footer" style={{ textAlign: 'center', padding: '2rem 1rem 1rem', marginTop: '2rem', borderTop: '1px solid #eee', fontSize: '0.625rem', color: '#666' }}>
          <p>
            New to the portal? Click{' '}
            <button
              onClick={() => setShowManualModal(true)}
              className="customer-manual-inline-link"
            >here</button>{' '}
            for the online manual to get assistance navigating the portal.
          </p>
          <p>
            By using this service, you agree to the{' '}
            <a href={`/BespokeBeauty/${urlSalonId}/admin/terms`} style={{ color: 'var(--salon-primary, #E64C31)', textDecoration: 'underline' }}>
              terms and conditions of usage
            </a>{' '}
            as well as our{' '}
            <a href={`/BespokeBeauty/${urlSalonId}/admin/privacy`} style={{ color: 'var(--salon-primary, #E64C31)', textDecoration: 'underline' }}>
              data privacy policy
            </a>.
          </p>
        </div>
      </main>

      {/* ── Pickup / Delivery fulfillment modal — shown before PayNow/Wallet ── */}
      {fulfillmentProduct && (
        <div className="paynow-qr-overlay" onClick={closeFulfillmentFlow}>
          <div className="paynow-qr-modal" onClick={e => e.stopPropagation()}>
            <button className="paynow-qr-close" onClick={closeFulfillmentFlow} aria-label="Close">✕</button>
            <h3 className="paynow-qr-title">{fulfillmentProduct.product.name}</h3>
            <p className="paynow-qr-price">${Number(fulfillmentProduct.product.price || 0).toFixed(2)}</p>

            {fulfillmentStep === 'choose' && (
              <>
                <p className="paynow-qr-instructions">
                  Product base location:<br />
                  <strong>{deliveryBaseLocation?.address || 'Not set by the salon'}</strong>
                </p>
                <p className="paynow-qr-instructions">How would you like to receive this product?</p>
                <div className="fulfillment-choice-row">
                  <button className="cd-product-paynow-btn" onClick={choosePickup}>Pick Up</button>
                  <button className="cd-product-paynow-btn" onClick={chooseDelivery}>Deliver to Me</button>
                </div>
              </>
            )}

            {fulfillmentStep === 'address' && (
              <>
                <p className="paynow-qr-instructions">Enter your delivery address:</p>
                <input
                  type="text"
                  className="delivery-address-input"
                  placeholder="e.g. 12 Marina Blvd, Singapore 018982"
                  value={deliveryAddressInput}
                  onChange={(e) => setDeliveryAddressInput(e.target.value)}
                />
                <p className="delivery-address-hint">
                  Please enter the Singapore postal code at the end of your delivery address and use the format : Singapore 123456
                </p>
                {deliveryCalcError &&<p className="paynow-qr-unavailable">{deliveryCalcError}</p>}
                <button
                  className="cd-product-paynow-btn"
                  style={{ marginTop: '12px' }}
                  disabled={deliveryCalculating}
                  onClick={handleCalculateDeliveryCharge}
                >
                  {deliveryCalculating ? 'Calculating...' : 'Calculate Delivery Charges'}
                </button>
              </>
            )}

            {fulfillmentStep === 'result' && deliveryQuote && (
              <>
                <p className="paynow-qr-instructions">
                  Delivery distance: <strong>{deliveryQuote.distanceKm} km</strong> ({deliveryQuote.tierName})<br />
                  Delivery charge: <strong>${Number(deliveryQuote.charge).toFixed(2)}</strong>
                </p>
                <p className="paynow-qr-instructions">Do you accept this delivery charge?</p>
                <div className="fulfillment-choice-row">
                  <button className="cd-product-paynow-btn" onClick={acceptDeliveryCharge}>Accept</button>
                  <button className="cd-product-enquiry-btn" onClick={rejectDeliveryCharge}>Reject</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Full-size product image viewer */}
      {productImagePreview && (
        <div className="cd-image-lightbox" onClick={() => setProductImagePreview(null)}>
          <button
            className="cd-image-lightbox-close"
            onClick={() => setProductImagePreview(null)}
            aria-label="Close image"
            autoFocus
          >
            ✕
          </button>
          <img
            src={productImagePreview.url}
            alt={productImagePreview.name}
            className="cd-image-lightbox-img"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* PayNow QR modal — only renders when PayNow is enabled for this salon */}
      {showPaynowModal && paynowEnabled && (
        <div className="paynow-qr-overlay" onClick={() => setShowPaynowModal(false)}>
          <div className="paynow-qr-modal" onClick={e => e.stopPropagation()}>
            <button className="paynow-qr-close" onClick={() => setShowPaynowModal(false)} aria-label="Close">✕</button>
            <h3 className="paynow-qr-title">
              {paynowModalProduct?.name ? `Buy: ${paynowModalProduct.name}` : 'PayNow'}
            </h3>
            {paynowModalProduct?.price && (
              <p className="paynow-qr-price">
                ${(Number(paynowModalProduct.price) + Number(paynowModalProduct.deliveryCharge || 0)).toFixed(2)}
              </p>
            )}
            {paynowModalProduct?.fulfillmentType === 'delivery' && (
              <p className="paynow-qr-instructions">
                {paynowModalProduct.deliveryChargeAccepted
                  ? `Includes $${Number(paynowModalProduct.deliveryCharge || 0).toFixed(2)} delivery charge (${paynowModalProduct.deliveryTierName}) to ${paynowModalProduct.deliveryAddress}`
                  : `Delivery to: ${paynowModalProduct.deliveryAddress} (no delivery charge)`}
              </p>
            )}
            {paynowQrUrl ? (
              <img src={paynowQrUrl} alt="PayNow QR Code" className="paynow-qr-image" />
            ) : (
              <p className="paynow-qr-unavailable">PayNow QR code not available. Please contact the salon.</p>
            )}
            <p className="paynow-qr-instructions">Scan this QR code with your banking app to pay.</p>
            {salonConfig?.email && (
              <p className="paynow-qr-screenshot-note">
                Upon successful payment, please send a screenshot from your bank app to{' '}
                <strong>{salonConfig.email}</strong>
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Pay with Wallet confirm modal ── */}
      {walletPayProduct && (
        <div className="paynow-qr-overlay" onClick={() => !walletPaying && setWalletPayProduct(null)}>
          <div className="paynow-qr-modal" onClick={e => e.stopPropagation()}>
            <button
              className="paynow-qr-close"
              onClick={() => setWalletPayProduct(null)}
              aria-label="Close"
              disabled={walletPaying}
            >
              ✕
            </button>
            <h3 className="paynow-qr-title">Pay with Wallet: {walletPayProduct.name}</h3>
            <p className="paynow-qr-price">
              ${(Number(walletPayProduct.price || 0) + Number(walletPayProduct.deliveryCharge || 0)).toFixed(2)}
            </p>
            {walletPayProduct.fulfillmentType === 'delivery' && (
              <p className="paynow-qr-instructions">
                {walletPayProduct.deliveryChargeAccepted
                  ? `Includes $${Number(walletPayProduct.deliveryCharge || 0).toFixed(2)} delivery charge (${walletPayProduct.deliveryTierName}) to ${walletPayProduct.deliveryAddress}`
                  : `Delivery to: ${walletPayProduct.deliveryAddress} (no delivery charge)`}
              </p>
            )}
            <p className="paynow-qr-instructions">
              Current balance: ${walletBalance.toFixed(2)}<br />
              New balance after payment: ${(walletBalance - Number(walletPayProduct.price || 0) - Number(walletPayProduct.deliveryCharge || 0)).toFixed(2)}
            </p>
            {walletPayError && (
              <p className="paynow-qr-unavailable">{walletPayError}</p>
            )}
            <button
              className="cd-product-paynow-btn"
              style={{ marginTop: '12px' }}
              disabled={walletPaying}
              onClick={handleConfirmPayWithWallet}
            >
              {walletPaying ? 'Processing...' : 'Confirm Payment'}
            </button>
          </div>
        </div>
      )}

      {/* ── Customer Training Manual viewer — view-only, no download ── */}
      {showManualModal && (
        <div
          className="customer-manual-overlay"
          onClick={() => setShowManualModal(false)}
          onContextMenu={e => e.preventDefault()}
        >
          <div
            className="customer-manual-container"
            onClick={e => e.stopPropagation()}
            onContextMenu={e => e.preventDefault()}
          >
            <div className="customer-manual-header">
              <span className="customer-manual-title">
                Customer Portal Training Manual v1.4
              </span>
              <span className="customer-manual-view-only-badge" aria-label="View only">
                👁 View only
              </span>
              <button
                className="customer-manual-close"
                onClick={() => setShowManualModal(false)}
                aria-label="Close manual"
              >
                ✕
              </button>
            </div>
            {/* Frame wrapper: overflow:hidden lets the Firefox-specific CSS
                shift the iframe up so its built-in PDF toolbar is clipped.
                Chrome/Edge already suppress the toolbar via #toolbar=0. */}
            <div
              className="customer-manual-frame-wrapper"
              onContextMenu={e => e.preventDefault()}
            >
              <iframe
                src="/BespokeBeauty_Customer_Portal_Training_Manual_v1.4.pdf#toolbar=0&navpanes=0&scrollbar=1"
                title="Customer Portal Training Manual v1.4"
                className="customer-manual-frame"
                onContextMenu={e => e.preventDefault()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerDashboard;